import { createTrackingEmitterHooks } from "@b2bportal/core-tracking";
import type {
  SupportHubCommonProperties,
  SupportHubDismissedProperties,
  SupportHubHelpLaunchedProperties,
  SupportHubViewedProperties,
} from "./supportHub";

export enum SelfServeEventName {
  confirm_resend_email = "confirm_resend_email",
  submitted_query = "submitted_query",
  support_hub_help_launched = "support_hub_help_launched",
  support_hub_viewed = "support_hub_viewed",
  support_hub_tapped_manage_booking = "support_hub_tapped_manage_booking",
  support_hub_tapped_faq = "support_hub_tapped_faq",
  support_hub_tapped_contact_support = "support_hub_tapped_contact_support",
  support_hub_dismissed = "support_hub_dismissed",
  tapped_suggestion_chip = "tapped_suggestion_chip",
  viewed_resend_email = "viewed_resend_email",
}

export enum SelfServeRawEventName {
  ConfirmResendEmail = "ConfirmResendEmail",
  SubmittedQuery = "SubmittedQuery",
  TappedSuggestionChip = "TappedSuggestionChip",
  ViewedResendEmailModal = "ViewedResendEmailModal",
}

export enum SupportHubRawEventName {
  SupportHubHelpLaunched = "SupportHubHelpLaunched",
  SupportHubViewed = "SupportHubViewed",
  SupportHubTappedManageBooking = "SupportHubTappedManageBooking",
  SupportHubTappedFaq = "SupportHubTappedFaq",
  SupportHubTappedContactSupport = "SupportHubTappedContactSupport",
  SupportHubDismissed = "SupportHubDismissed",
}

export type SelfServeConfirmResendEmailRawEvent = {
  name: SelfServeRawEventName.ConfirmResendEmail;
  properties: Record<string, unknown>;
};

export type SelfServeSubmittedQueryRawEvent = {
  name: SelfServeRawEventName.SubmittedQuery;
  properties: Record<string, unknown>;
};

export type SelfServeTappedSuggestionChipRawEvent = {
  name: SelfServeRawEventName.TappedSuggestionChip;
  properties: Record<string, unknown>;
};

export type SelfServeViewedResendEmailModalRawEvent = {
  name: SelfServeRawEventName.ViewedResendEmailModal;
  properties: Record<string, unknown>;
};

export type SupportHubHelpLaunchedRawEvent = {
  name: SupportHubRawEventName.SupportHubHelpLaunched;
  properties: SupportHubHelpLaunchedProperties;
};

export type SupportHubViewedRawEvent = {
  name: SupportHubRawEventName.SupportHubViewed;
  properties: SupportHubViewedProperties;
};

export type SupportHubTappedManageBookingRawEvent = {
  name: SupportHubRawEventName.SupportHubTappedManageBooking;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedFaqRawEvent = {
  name: SupportHubRawEventName.SupportHubTappedFaq;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedContactSupportRawEvent = {
  name: SupportHubRawEventName.SupportHubTappedContactSupport;
  properties: SupportHubCommonProperties;
};

export type SupportHubDismissedRawEvent = {
  name: SupportHubRawEventName.SupportHubDismissed;
  properties: SupportHubDismissedProperties;
};

export type SupportHubRawEvent =
  | SupportHubHelpLaunchedRawEvent
  | SupportHubViewedRawEvent
  | SupportHubTappedManageBookingRawEvent
  | SupportHubTappedFaqRawEvent
  | SupportHubTappedContactSupportRawEvent
  | SupportHubDismissedRawEvent;

export type SelfServeRawEvent =
  | SelfServeConfirmResendEmailRawEvent
  | SelfServeSubmittedQueryRawEvent
  | SelfServeTappedSuggestionChipRawEvent
  | SelfServeViewedResendEmailModalRawEvent
  | SupportHubRawEvent;

export type SelfServeConfirmResendEmailEvent = {
  name: SelfServeEventName.confirm_resend_email;
  properties: Record<string, unknown>;
};

export type SelfServeSubmittedQueryEvent = {
  name: SelfServeEventName.submitted_query;
  properties: Record<string, unknown>;
};

export type SelfServeTappedSuggestionChipEvent = {
  name: SelfServeEventName.tapped_suggestion_chip;
  properties: Record<string, unknown>;
};

export type SelfServeViewedResendEmailEvent = {
  name: SelfServeEventName.viewed_resend_email;
  properties: Record<string, unknown>;
};

export type SupportHubHelpLaunchedEvent = {
  name: SelfServeEventName.support_hub_help_launched;
  properties: SupportHubHelpLaunchedProperties;
};

export type SupportHubViewedEvent = {
  name: SelfServeEventName.support_hub_viewed;
  properties: SupportHubViewedProperties;
};

export type SupportHubTappedManageBookingEvent = {
  name: SelfServeEventName.support_hub_tapped_manage_booking;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedFaqEvent = {
  name: SelfServeEventName.support_hub_tapped_faq;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedContactSupportEvent = {
  name: SelfServeEventName.support_hub_tapped_contact_support;
  properties: SupportHubCommonProperties;
};

export type SupportHubDismissedEvent = {
  name: SelfServeEventName.support_hub_dismissed;
  properties: SupportHubDismissedProperties;
};

export type SupportHubHTSEvent =
  | SupportHubHelpLaunchedEvent
  | SupportHubViewedEvent
  | SupportHubTappedManageBookingEvent
  | SupportHubTappedFaqEvent
  | SupportHubTappedContactSupportEvent
  | SupportHubDismissedEvent;

export type SelfServeHTSEvent =
  | SelfServeConfirmResendEmailEvent
  | SelfServeSubmittedQueryEvent
  | SelfServeTappedSuggestionChipEvent
  | SelfServeViewedResendEmailEvent
  | SupportHubHTSEvent;

export const {
  useEmitRawEvent: useEmitCoreSelfServeRawEvent,
  useEmitRawEventOnce: useEmitCoreSelfServeRawEventOnce,
} = createTrackingEmitterHooks<SelfServeRawEvent>();
